import { inject, Injectable, DOCUMENT } from '@angular/core';
import { filter, firstValueFrom, map, merge, Subject, take } from 'rxjs';
import { MessageService } from 'src/app/modules/message/services/message/message.service';
import { SpeechSynthesisService } from 'src/app/modules/speech-synthesis/services/speech-synthesis/speech-synthesis.service';
import { MessageCodes } from 'src/app/shared/enums';
import { AudioData } from 'src/app/shared/interfaces';
import { SettingsService } from 'src/app/shared/services/settings/settings.service';
import { TrackingService } from '../../../tracking/services/tracking.service';
import { speechSynthesisErrorMessages } from '../../data/error-messages';

import { replaceSpecialCharacters } from '../tts-utils';
import { TTSReadAloudService } from '../read-aloud.service';
import { VerbalService } from '../verbal.service';
import { AudioService } from 'src/app/modules/audio/services/audio/audio.service';
import { parseAndGetLastValueAfterToken } from 'src/app/shared/utils/util';

/**
 * Service to interface between the read aloud service and the speech synthesis service
 */
@Injectable({
  providedIn: 'root',
})
export class SpeechSynthesisReadAloudService implements TTSReadAloudService {
  playbackEndId: Subject<string> = new Subject<string>();
  playbackStartId: Subject<string> = new Subject<string>();
  currentId = '';
  currentlyPaused = false;
  currentTryCount = 0;
  retryInterval = 50; // milliseconds
  maxTries = 3;
  currentStarted = false;
  currentlyRetrying = false;
  retryTimeout!: NodeJS.Timeout;

  private speechSynthesisService: SpeechSynthesisService = inject(
    SpeechSynthesisService
  );
  private settingsService: SettingsService = inject(SettingsService);
  private messageService: MessageService = inject(MessageService);
  private trackingService: TrackingService = inject(TrackingService);
  private document: Document = inject(DOCUMENT);
  private verbalService = inject(VerbalService);
  private audioService = inject(AudioService);

  async play(audioData: AudioData): Promise<void> {
    if (!audioData.text || this.currentlyRetrying) return;
    if (this.currentId !== audioData.id) {
      this.currentId = audioData.id;
      this.currentTryCount = 1;
      this.currentStarted = false;
      this.currentlyRetrying = false;
    } else {
      if (this.currentTryCount < this.maxTries) {
        this.currentTryCount++;
      }
    }

    const processedAudioData = await this.verbalService.processLongText(
      audioData.text,
      audioData.id
    );

    // Check if processedAudioData is an AudioDataSequence
    if ('parts' in processedAudioData) {
      // It's an AudioDataSequence
      processedAudioData.parts.forEach(async part => {
        if (part.containsPhonemes) {
          await this.verbalService.loadPhoneme(part);
        }
      });
      await this.playSequence(processedAudioData.parts);
      return;
    }

    this.speechSynthesisService.speak(
      replaceSpecialCharacters(
        processedAudioData.text || audioData.text,
        this.document
      ),
      {
        onStartCallback: (): void => {
          this.playbackStartId.next(this.currentId);
          this.currentStarted = true;
        },
        onEndCallback: (): void => {
          if (!this.currentStarted) {
            console.error(
              'SpeechSynthesis onEndCallback was called before onStartCallback, assume tab is muted.'
            );
            this.retryPlayOrError(audioData, { error: 'tab-muted' });
          } else {
            this.sendPlaybackEndId();
          }
        },
        onErrorCallback: (event: SpeechSynthesisErrorEvent): void => {
          if (this.shouldIgnoreError(event.error)) return;
          console.error('SpeechSynthesisErrorEvent', event);
          this.retryPlayOrError(audioData, event);
        },
      }
    );
  }

  /**
   * Plays a sequence of audio parts in order, handling both phoneme-based and regular text-based audio.
   * @param parts - An array of AudioData objects to be played sequentially
   * @returns Promise<void> that resolves when the entire sequence has finished playing
   * @description
   * This method handles two types of audio playback:
   * 1. Phoneme-based audio (when part.containsPhonemes is true)
   * 2. Regular text-to-speech (when part.containsPhonemes is false)
   *
   * For each part in the sequence:
   * - Waits for the current part to finish before playing the next
   * - Adds a 500ms pause between parts for natural spacing
   * - Handles error cases for both phoneme and regular playback
   * - Emits playback start and end events
   *
   * @throws Error if any part fails to play, with the specific part ID in the error message
   */
  async playSequence(parts: AudioData[]): Promise<void> {
    if (!parts || parts.length === 0) return;
    this.playbackStartId.next(this.currentId); // Signal that the sequence is starting
    this.currentStarted = true;
    for (const part of parts) {
      const end$ = (
        part.containsPhonemes
          ? this.verbalService.phonemeEnded$
          : this.playbackEndId
      ).pipe(
        filter(id => {
          return part.containsPhonemes
            ? parseAndGetLastValueAfterToken(id, '|').remStr === part.id
            : id === part.id;
        }),
        map(() => true),
        take(1)
      );

      // Stream that throws when THIS part fails
      const err$ = this.verbalService.phonemeError$.pipe(
        filter(({ id }) => {
          return part.containsPhonemes
            ? parseAndGetLastValueAfterToken(id, '|').remStr === part.id
            : id === part.id;
        }),
        map(({ error }) => {
          throw new Error(`Error playing part ${part.id}`, { cause: error });
        }),
        take(1)
      );

      if (part.containsPhonemes) {
        this.audioService.playSound(part, false, false, true);
      } else {
        this.speechSynthesisService.speak(
          replaceSpecialCharacters(part.text as string, this.document),
          {
            onEndCallback: (): void => {
              this.playbackEndId.next(part.id);
            },
          }
        );
      }
      await firstValueFrom(merge(end$, err$)); // wait for any of the two streams to emit a value
      // Add a small pause between parts for natural spacing
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    this.sendPlaybackEndId(); // Signal that the entire sequence has ended
  }

  retryPlayOrError(
    audioData: AudioData,
    event: SpeechSynthesisErrorEvent | { error: string }
  ): void {
    if (this.currentTryCount < 3) {
      this.currentlyRetrying = true;
      if (this.retryTimeout) clearTimeout(this.retryTimeout);
      this.retryTimeout = setTimeout(() => {
        this.currentlyRetrying = false;
        this.play(audioData);
      }, this.retryInterval);
    } else {
      this.handleError(event);
    }
  }

  handleError(event: SpeechSynthesisErrorEvent | { error: string }): void {
    this.sendPlaybackEndId();
    const messageToShow = speechSynthesisErrorMessages[event.error];
    if (messageToShow) {
      this.messageService.showMessage(messageToShow, () => {
        if (messageToShow !== MessageCodes.SPEECH_SYNTHESIS_TEXT_TOO_LONG)
          this.settingsService.saveSettings({ readAloud: false }); // toggle off ReadAloud
      });
    } else {
      console.error(
        `Speech Synthesis Read Aloud error doesn't have a message to show ;_;`,
        event
      );
    }
    this.trackError(event.error);
  }

  // Certain errors are expected behavior
  shouldIgnoreError(error: string): boolean {
    switch (error) {
      case 'canceled':
      case 'interrupted':
        return true;
      default:
        return false;
    }
  }

  trackError(error: string): void {
    this.trackingService.push({
      event: 'Read Aloud Error',
      error: error,
    });
  }

  sendPlaybackEndId(): void {
    this.playbackEndId.next(this.currentId);
    this.currentId = '';
  }

  stop(): void {
    this.speechSynthesisService.stop();
    this.currentlyPaused = false;
    this.sendPlaybackEndId();
  }

  pause(): void {
    this.speechSynthesisService.pause();
    this.currentlyPaused = !this.currentlyPaused;
  }

  togglePause(): void {
    if (this.currentlyPaused) {
      this.resume();
    } else {
      this.pause();
    }
  }

  resume(): void {
    this.speechSynthesisService.resume();
    this.currentlyPaused = !this.currentlyPaused;
  }
}
